<template>
  <div class="business-profile-section" data-track="Business Profile">
    <!-- Header Section Begins -->
    <div class="level page-info-section">
      <div class="level-left">
        <b class="page-heading">Your Public Profile</b>
      </div>
    </div>
    <!-- Header Section Ends -->
    <hr class="horizontal-row"/>
    <!-- Main section Begins -->
    <div class="columns">
      <div class="column detail-section">
        <!-- Preview Section Begins -->
        <!-- Logo and Description Section Begins -->
        <div class="columns detail-section-row">
          <div class="column logo-column is-one-fifth p-0">
            <div>
              <div class="columns">
                <div class="column company-name-mobile">
                  <img :height="business_info.org_logo_height"
                       :src="business_info.org_logo"
                       :width="business_info.org_logo_width"
                       class="org_logo"
                  >
                  <div class="intro is-hidden-desktop is-hidden-tablet">
                    <span><b>{{ business_info.name }}</b></span><br>
                    <p class="is-flex">
                      Upload your company logo &nbsp;
                      <tooltip-new content-width="155px">
                        Formats: PNG, JPEG
                        Dimension: 200 x 200
                        Max file size is 5 MB.
                      </tooltip-new>
                    </p>
                  </div>
                </div>
              </div>
              <div class="upload-avtar is-hidden-mobile">
                Upload your<br> company logo
                <tooltip-new content-width="155px">
                  Formats: PNG, JPEG
                  Dimension: 200 x 200
                  Max file size is 5 MB.
                </tooltip-new>
              </div>
              <avatar-cropper
                :labels="labels"
                :upload-form-data="form_data"
                :upload-headers="upload_headers"
                :upload-url="upload_url"
                mimes="image/png,image/gif,image/jpeg"
                trigger="#pick_logo"
                @completed="handleCompleted"
                @uploading="handleUploading"
              >
              </avatar-cropper>
              <div class="has-text-centered">
                <button
                  v-show="!is_loading_org"
                  id="pick_logo"
                  :class="{'is-loading': is_uploading_logo}"
                  class="button-edit is-button-grey circle-border pick-logo-image"
                  data-track="form: business_logo"
                >
                  <i class="fas fa-pencil-alt"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="column description-section">
            <div class="columns is-flex-mobile text-row" @click="handleDropbox('description')">
              <label
                :class="[{'active-dropbox': dropbox.description}]"
                class="column label is-hidden-desktop is-hidden-tablet padding-left-0 padding-bottom-0"
              >
                Business Description
              </label>
              <span class="column is-half has-text-right is-hidden-desktop is-hidden-tablet padding-bottom-0">
                <img :src="`/static/img/ic-${!dropbox.description ? 'right' : 'down'}-arrow.svg`" alt="open">
              </span>
            </div>
            <div :class="[{'social-info': !dropbox.description}]">
              <p class="description-first-text is-hidden-mobile"><b>{{ business_info.name }}</b></p>
              <p class="description-second-text">Tell our Users, and your future customers, about your business</p>
              <div class="description-text">
                <textarea
                  v-model="business_info.org_description"
                  :maxlength="maxlength"
                  class="description"
                  data-track="form: business_description"
                  placeholder="Enter a description about your company"
                >
                </textarea>
                <span class="words-left">Limit: {{ maxlength }} characters. Remaining {{ descriptionWordsLeft }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Logo and Description Section Ends -->
        <!-- Industry section Begins -->
        <div class="columns industries-section">
          <div class="columns is-flex-mobile is-hidden-desktop is-hidden-tablet text-row"
               @click="handleDropbox('industry')">
            <label :class="[{'active-dropbox': dropbox.industry}]"
                   class="column label padding-left-0 padding-bottom-0 is-flex">
              Industry &nbsp;
              <tooltip-new content-width="325px">
                Allow current, and potential, consumers to find your business when searching by industry. It highlights
                the performance of your business against your industry competitors, as well as improves the
                discoverability of your company.
              </tooltip-new>
            </label>
            <span class="column is-half has-text-right is-hidden-desktop is-hidden-tablet padding-bottom-0">
              <img :src="`/static/img/ic-${!dropbox.industry ? 'right' : 'down'}-arrow.svg`" alt="open">
            </span>
          </div>
          <!-- Industries -->
          <div :class="[{'social-info': !dropbox.industry}]"
               class="column padding-left-0 padding-right-0 padding-top-0">
            <div v-if="!is_loading_org" class="field">
              <div class="control industries">
                <label class="label is-hidden-mobile is-flex">
                  Industry &nbsp;
                  <tooltip-new content-width="325px">
                    Allow current, and potential, consumers to find your business when searching by industry. It
                    highlights the performance of your business against your industry competitors, as well as improves
                    the discoverability of your company.
                  </tooltip-new>
                </label>
                <div
                  :class="{'is-active' : show_dropdown}"
                  class="dropdown"
                  @mouseleave="hideDropdown()"
                >
                  <div class="dropdown-trigger">
                    <button
                      aria-controls="dropdown-menu3"
                      aria-haspopup="true"
                      class="button-dropdown"
                      @click.prevent="showDropdown()"
                    >
                      <span ref="industry_name" class="is-size-6">{{ industry.name }}</span>
                      <span class="icon is-small">
                          <i aria-hidden="true" class="fas fa-angle-down"></i>
                        </span>
                    </button>
                  </div>
                  <div class="dropdown-menu" role="menu">
                    <div class="dropdown-content is-paddingless">
                      <a v-for="industry in industries" :ref="`${industry.id}`"
                         class="dropdown-item"
                         href="#"
                         @click.prevent="selectIndustry(industry)"
                      >
                        {{ industry.name }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Industries -->
          <!-- Sub-industries -->
          <div :class="[{'social-info': !dropbox.industry}]" class="column padding-left-0 is-half padding-top-0">
            <div v-if="subIndustries && subIndustries.length" class="field">
              <div class="control subcategories">
                <label :class="[{'active-dropbox': dropbox.industry}]" class="label margin-0 is-flex">
                  Sub-Categories &nbsp;
                </label>
                <div class="cat-list">
                  <div class="columns">
                    <div
                      v-for="(subIndustry, index) in subIndustries"
                      class="column is-half is-narrow"
                    >
                      <input
                        :key="index"
                        v-model="business_info.sub_industries"
                        :value="subIndustry.id"
                        class="cat-check"
                        type="checkbox"
                      >
                      <span class="checkmark"></span>
                      <span class="cat-name">{{ subIndustry.name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Sub-industries -->
        </div>
        <!-- Industry section Ends -->
        <!-- Contact section Begins -->
        <div class="contact-section">
          <div class="columns is-flex-mobile text-row" @click="handleDropbox('contactUs')">
            <label :class="[{'active-dropbox': dropbox.contactUs}]"
                   class="column label padding-left-0 padding-bottom-0">
              Contact Us
              <p class="brief is-hidden-mobile">Add as much information about your business as possible, so our Users
                can find your business outside of Hellopeter.</p>
            </label>
            <span class="column is-half has-text-right is-hidden-desktop is-hidden-tablet padding-bottom-0">
              <img :src="`/static/img/ic-${!dropbox.contactUs ? 'right' : 'down'}-arrow.svg`" alt="open">
            </span>
          </div>
          <div :class="[{'social-info': !dropbox.contactUs}]" class="columns address-info">
            <p class="column brief is-hidden-desktop is-hidden-tablet">
              Add as much information about your business as possible, so our Users can find your business outside of
              Hellopeter.
            </p>
            <div class="column padding-left-0">
              <div class="field">
                <div class="control">
                  <label class="label">Physical Address</label>
                  <vue-google-autocomplete
                    id="address"
                    ref="address"
                    :country="['za']"
                    classname="input"
                    data-track="form: business_address"
                    placeholder="Physical address"
                    v-on:change="checkAddressIsNull"
                    v-on:placechanged="getAddressData"
                  >
                  </vue-google-autocomplete>
                </div>
              </div>
              <div class="field">
                <div class="control">
                  <label class="label">Website</label>
                  <input v-model="business_info.org_url" class="input" data-track="form: business_public_website"
                         placeholder="Website" type="text">
                </div>
                <p v-if="form.errors.has('org_url')" class="help is-danger" v-text="form.errors.get('org_url')"></p>
              </div>
              <div class="field">
                <div class="control">
                  <label class="label is-flex-align-center"><span :class="[{'disabled': !subscriptionType}]">Public Email</span>
                    <span v-if="!subscriptionType" class="upgrade-section">
                      <img alt="lock" class="fa-lock" src="/static/img/free-lock-icon.png"/> <router-link
                      :to="`/billing/edit-plan/g1/monthly`">Upgrade</router-link> to add your public profile
                    </span>
                  </label>
                  <input
                    v-model="business_info.public_email"
                    :disabled="!subscriptionType"
                    class="input"
                    data-track="form: business_public_email"
                    placeholder="Public email"
                    type="text"
                  >
                </div>
                <p v-if="form.errors.has('public_email')" class="help is-danger"
                   v-text="form.errors.get('public_email')"></p>
              </div>
              <div class="field">
                <div class="control">
                  <label class="label is-flex-align-center"><span :class="[{'disabled': !subscriptionType}]">Public Contact Number</span>
                    <span v-if="!subscriptionType" class="upgrade-section"><img alt="lock"
                                                                                class="fa-lock"
                                                                                src="/static/img/free-lock-icon.png"/> <router-link
                      :to="`/billing/edit-plan/g1/monthly`">Upgrade</router-link> to add your public profile
                    </span>
                  </label>
                  <input
                    v-model="business_info.public_tel"
                    :disabled="!subscriptionType"
                    class="input"
                    data-track="form: business_tel"
                    placeholder="Public contact number"
                    type="text"
                  >
                </div>
                <p v-if="form.errors.has('public_tel')" class="help is-danger"
                   v-text="form.errors.get('public_tel')"></p>
              </div>
            </div>
            <div class="column map-area">
              <div class="custom-marker-tooltip">
                <div class="columns">
                  <div class="column padding-bottom-0 padding-left-0 padding-top-0"><b>{{ business_info.name }}</b>
                  </div>
                </div>
                <div class="columns">
                  <div class="column padding-bottom-0 padding-left-0">{{ business_info.physical_address }}</div>
                </div>
                <div class="columns">
                  <div class="column padding-left-0 padding-bottom-0">{{ business_info.public_email }}</div>
                </div>
                <div class="columns">
                  <div class="column padding-left-0">{{ business_info.public_tel }}</div>
                </div>
              </div>
              <figure class="image is-5by3">
                <img ref="googleMap" class="map" height="410" width="625">
              </figure>
            </div>
          </div>
        </div>
        <!-- Contact section Ends -->
        <!-- Online profile section Begins -->
        <div class="online-profile-section">
          <div class="columns text-row" @click="handleDropbox('onlineProfile')">
            <label class="column label is-half padding-left-0 padding-bottom-0 is-flex-align-center">
              <span :class="[{'active-dropbox': dropbox.onlineProfile}, 'padding-bottom-0']">Online Profile</span>
              <span v-if="!subscriptionType" class="upgrade-section is-hidden-mobile">
                <img alt="lock" class="fa-lock" src="/static/img/free-lock-icon.png"/> <router-link
                :to="`/billing/edit-plan/g1/monthly`">Upgrade</router-link> to add your public profile
              </span>
            </label>
            <span
              class="column is-half has-text-right is-hidden-desktop is-hidden-tablet padding-left-75 padding-bottom-0">
              <img :src="`/static/img/ic-${!dropbox.onlineProfile ? 'right' : 'down'}-arrow.svg`" alt="open">
            </span>
          </div>
          <div :class="[{'social-info': !dropbox.onlineProfile}, 'social-media-apps']">
            <span v-if="!subscriptionType" class="upgrade-section is-hidden-tablet is-hidden-desktop">
              <img alt="lock" class="fa-lock" src="/static/img/free-lock-icon.png"/> <router-link
              :to="`/billing/edit-plan/g1/monthly`">Upgrade</router-link> to add your public profile
            </span>
            <div class="columns">
              <div class="column is-half padding-left-0">
                <div class="columns control has-icons-left">
                  <span :class="[{'disabled': !subscriptionType}]" class="column is-small related-icon">
                    <img alt="facebook" src="/static/img/facebook.png">
                  </span>
                  <input
                    v-model="business_info.org_facebook"
                    :disabled="!subscriptionType"
                    class="column input"
                    data-track="form: business_facebook"
                    placeholder="Add your Facebook URL"
                    type="text"
                  >
                </div>
                <p v-if="form.errors.has('org_url')" class="help is-danger" v-text="form.errors.get('org_url')"></p>
              </div>
              <div class="column is-half padding-left-0">
                <div class="field">
                  <div class="columns control has-icons-left">
                    <span :class="[{'disabled': !subscriptionType}]" class="column is-small related-icon">
                      <img alt="insta" src="/static/img/insta.png">
                    </span>
                    <input
                      v-model="business_info.org_instagram"
                      :disabled="!subscriptionType"
                      class="column input"
                      data-track="form: business_instagram"
                      placeholder="Add your Instagram URL"
                      type="text">
                  </div>
                  <p v-if="form.errors.has('org_instagram')" class="help is-danger"
                     v-text="form.errors.get('org_instagram')"></p>
                </div>
              </div>
            </div>
            <div class="columns">
              <div class="column is-half padding-left-0">
                <div class="field">
                  <div class="columns control has-icons-left">
                    <span :class="[{'disabled': !subscriptionType}]" class="column is-small related-icon">
                      <img alt="linkedin" src="/static/img/linkedin.png">
                    </span>
                    <input
                      v-model="business_info.org_linkedin"
                      :disabled="!subscriptionType"
                      class="column input"
                      data-track="form: business_linkedin"
                      placeholder="Add your LinkedIn URL"
                      type="text"
                    >
                  </div>
                  <p v-if="form.errors.has('org_linkedin')" class="help is-danger"
                     v-text="form.errors.get('org_linkedin')"></p>
                </div>
              </div>
              <div class="column is-half padding-left-0">
                <div class="field">
                  <div class="columns control has-icons-left">
                    <span :class="[{'disabled': !subscriptionType}]" class="column is-small related-icon">
                      <img alt="twitter" src="/static/img/twitter.png">
                    </span>
                    <input
                      v-model="business_info.org_twitter"
                      :disabled="!subscriptionType"
                      class="column input"
                      data-track="form: business_twitter"
                      placeholder="Add your Twitter URL"
                      type="text"
                    >
                  </div>
                  <p v-if="form.errors.has('org_twitter')" class="help is-danger"
                     v-text="form.errors.get('org_twitter')"></p>
                </div>
              </div>
            </div>
            <div class="columns">
              <div class="column is-half padding-left-0">
                <div class="field">
                  <div class="columns control has-icons-left">
                    <span :class="[{'disabled': !subscriptionType}]" class="column is-small related-icon">
                      <img alt="google" src="/static/img/google.png">
                    </span>
                    <input
                      v-model="business_info.org_googleplus"
                      :disabled="!subscriptionType"
                      class="column input"
                      data-track="form: business_google_plus"
                      placeholder="Add your Google URL"
                      type="text"
                    >
                  </div>
                  <p v-if="form.errors.has('org_googleplus')" class="help is-danger"
                     v-text="form.errors.get('org_googleplus')"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Online profile section Ends -->
        <!-- Preview Section Ends -->
      </div>
      <div class="column action-section is-one-quarter padding-right-0">
        <div class="columns">
          <div class="column is-half-mobile padding-top-0 padding-right-0">
            <a
              :href="`${frontendURL}`"
              class="button is-button-dark-blue-outline is-uppercase"
              target="_blank"
            >Preview</a>
          </div>
          <div class="column is-half-mobile padding-top-0 padding-right-0">
            <button
              :class="[{ 'is-loading': is_sending}]"
              :disabled="is_sending || nothings_changed"
              class="button is-button-dark-blue is-uppercase"
              @click.prevent="saveBusinessDetails"
            >Save
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Main section Ends -->
    <!-- Confirmation Modal -->
    <ModalPopup
      :primaryBtnAction="redirectRoute"
      :secondaryBtnAction="stayOnPage"
      body="You have unsaved changes on this page. If you navigate away, your changes will be lost."
      primaryBtnText="Leave page"
      secondaryBtnText="Stay on this page"
      title="Are you sure?"
    />
    <!-- Confirmation Modal -->
  </div>
</template>

<script>
/* eslint-disable indent,no-new,no-undef */
import axios from 'axios'
import { TooltipNew } from '@/widgets'
import misc from '@/mixins/misc.js'
import { mapState, mapActions } from 'vuex'
import ApiResource from '@/api/ApiResource'
import AvatarCropper from 'vue-avatar-cropper'
import { SHOW_MODAL } from '@/store/action.types'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
import { NpsCircle, Rating, FlashMessage, ModalPopup } from '@/components'

export default {
  metaInfo: {
    title: 'Business Portal | Hellopeter - Your business profile'
  },
  components: {
    Rating,
    NpsCircle,
    TooltipNew,
    ModalPopup,
    ApiResource,
    FlashMessage,
    AvatarCropper,
    VueGoogleAutocomplete
  },
  mixins: [misc],
  data () {
    return {
      npsTotal: 0,
      maxlength: 250,
      routeToRedirect: '',
      businessSlug: localStorage.org_slug,
      globalTrustIndex: 0.0,
      globalIndustryRank: '',
      globalIndustryName: '',
      totalReviews: 0,
      avgRatingStats: '',
      map_url: '',
      is_sending: false,
      nothings_changed: true,
      is_loading_org: false,
      is_uploading_logo: false,
      avg_rating: '',
      new_org_logo: undefined,
      upload_url: `${process.env.API_URL}/business/${localStorage.org_slug}/upload-logo`,
      upload_headers: {
        'Authorization': 'Bearer ' + localStorage.auth_token,
        'Current-Business-Slug': localStorage.org_slug
      },
      labels: {
        submit: 'Crop',
        cancel: 'Cancel'
      },
      form_data: {
        business_slug: localStorage.org_slug
      },
      business_info: {
        name: '',
        org_description: '',
        org_facebook: '',
        org_twitter: '',
        org_linkedin: '',
        org_url: '',
        org_instagram: '',
        org_logo: '',
        org_logo_width: '',
        org_logo_height: '',
        org_googleplus: '',
        physical_address: '',
        public_email: '',
        public_tel: '',
        org_lat: '',
        org_lng: '',
        slug: localStorage.org_slug,
        sub_industries: []
      },
      industry: {},
      industries: [],
      subIndustries: [],
      show_dropdown: false,
      form: new ApiResource({}),
      currentActiveSubscription: '',
      dropbox: {
        description: false,
        industry: false,
        contactUs: false,
        onlineProfile: false
      }
    }
  },
  computed: {
    ...mapState({
      modal: state => state.app.modal
    }),
    /**
     * Check users current active subscription
     * @return {void}
     */
    isFreeSubscription () {
      return this.currentActiveSubscription.toLowerCase() === 'free'
    },
    /**
     * Check how many words left
     * @return {Number}
     */
    descriptionWordsLeft () {
      return this.maxlength - (this.business_info.org_description ? this.business_info.org_description.length : 0) || 0
    },
    /**
     * Check the user type whether it is of Enterprise/Pro
     * @return {boolean}
     */
    subscriptionType () {
      // If user having Enterprise/Pro business
      return this.userInfo.subscription.name !== 'Free'
    },
    /**
     * Front end URL
     * @return {String}
     */
    frontendURL () {
      return `${process.env.HP_APP_URL}/${localStorage.org_slug}`
    }
  },
  created () {
    this.is_loading_org = true
    this.getBusinessDetails()
    this.getBusinessStats()
    this.getBusinessBillingDetails()
    this.getIndustries()

    // Adding the unload event, when user want to leave unsaved changes
    window.addEventListener('beforeunload', (event) => {
      // If user changed the form and is Enterprise/Pro user
      if (!this.nothings_changed) {
        event.preventDefault()
        event.returnValue = 'You have unsaved changes. If you close this tab, your changes will be lost.'
      }
    })
    // Setting up object to get if page loaded first time
    if (!JSON.parse(localStorage.getItem('admin')).isProfileLoadedFirstTime) {
      let adminObj = {}
      Object.assign(adminObj, { ...JSON.parse(localStorage.getItem('admin')), isProfileLoadedFirstTime: true })
      localStorage.setItem('admin', JSON.stringify(adminObj))
      window.location.reload()
    }
  },
  mounted () {
    this.$gtm.trackEvent({
      event: 'pageLoad',
      virtualPageUrl: this.$router.currentRoute.path,
      virtualPageTitle: 'Your business profile'
    })
  },
  methods: {
    ...mapActions({
      'showModal': `app/${SHOW_MODAL}`
    }),
    submit () {
      this.is_uploading_logo = true
    },
    handleUploading () {
      this.is_uploading_logo = true
    },
    handleCompleted (response) {
      this.is_uploading_logo = false
      this.business_info.org_logo = response.business_logo + '?' + Math.floor((Math.random() * 100) + 1)
      localStorage.setItem('org_logo', this.business_info.org_logo)
      window.events.$emit('businessLogoChanged')
    },
    getAddressData: function (addressData, placeResultData, id) {
      this.business_info.org_lng = addressData.longitude
      this.business_info.org_lat = addressData.latitude
      this.business_info.physical_address = placeResultData.formatted_address

      this.updateMap(this.business_info.org_lat, this.business_info.org_lng)
    },
    checkAddressIsNull (data) {
      if (data.length === 0) {
        this.business_info.physical_address = ''
      }
    },
    /**
     * Gets info on business
     * @returns {void}
     */
    getBusinessDetails () {
      this.form.send(`/business/${this.business_info.slug}`, {}, 'get')
        .then((business) => {
          this.business_info.name = business.name
          this.business_info.org_description = business.org_description
          this.business_info.org_facebook = this.subscriptionType ? business.org_facebook : ''
          this.business_info.org_twitter = this.subscriptionType ? business.org_twitter : ''
          this.business_info.org_linkedin = this.subscriptionType ? business.org_linkedin : ''
          this.business_info.org_url = business.org_url
          this.business_info.org_instagram = this.subscriptionType ? business.org_instagram : ''
          this.business_info.org_logo = !business.org_logo ? 'https://bulma.io/images/placeholders/256x256.png' : business.org_logo
          this.business_info.org_logo_width = 105
          this.business_info.org_logo_height = 105
          this.business_info.org_googleplus = this.subscriptionType ? business.org_googleplus : ''
          this.business_info.physical_address = business.physical_address
          this.business_info.public_email = this.subscriptionType ? business.public_email : ''
          this.business_info.public_tel = this.subscriptionType ? business.public_tel : ''
          this.business_info.org_lat = business.org_lat
          this.business_info.org_lng = business.org_lng
          this.business_info.industry_id = business.industry.industry_id
          this.business_info.sub_industries = business.sub_industries.map(subIndustry => subIndustry.id)
          this.industry.slug = business.industry.slug
          this.industry.name = business.industry.name
          this.subIndustries = business.industry.sub_industries
          this.updateMap(this.business_info.org_lat, this.business_info.org_lng)
          this.is_loading_org = false
          this.$watch('business_info', () => {
            this.nothings_changed = false
          }, { deep: true })
        })
    },
    saveBusinessDetails () {
      this.is_sending = true

      const si = this.subIndustries.filter(subIndustryDetail => this.business_info.sub_industries.includes(subIndustryDetail.id)).map(subIndustryDetail => subIndustryDetail.id)
      this.business_info.sub_industries = si

      this.form.send(`/business/${this.business_info.slug}`, this.business_info)
        .then(() => {
          this.is_sending = false
          this.nothings_changed = true
          flash('Business profile updated successfully.')
          window.scrollTo(0, 0)
          dataLayer.push({
            'userMessage': {
              'messageType': 'success',
              'messageName': 'Successfully updated business profile on Business Profile view',
              'displayedMessage': 'Business profile updated successfully.'
            }
          })
          this.recacheOnBizModified([this.business_info.slug], [this.industry.slug])
        })
        .catch((response) => {
          this.is_sending = false
          window.scrollTo(0, 0)
          Object.keys(response).forEach((key) => {
            flash(response[key][0], 'is-danger')
          })
          dataLayer.push({
            'userMessage': {
              'messageType': 'error',
              'messageName': 'Failed to update business profile on Business Profile view',
              'displayedMessage': this.form.errors.getDefaultErrorMessage()
            }
          })
        })
    },
    updateMap (lat, lng) {
      this.map_url = `https://maps.googleapis.com/maps/api/staticmap?visual_refresh=true&center=${lat},${lng}
          &zoom=17&size=600x445&maptype=roadmap
          &markers=label:|${lat},${lng}&key=AIzaSyATQx52byjPXOYEjq0U8tOrJAO6a-H1lKs
        `
      this.$refs.googleMap.src = this.map_url
      this.$refs.address.update(this.business_info.physical_address)
    },
    getBusinessStats () {
      const errMessage = 'We encountered a problem in an attempt to retrieve your business stats, please try again later.'
      axios.get(`/widgets/${this.businessSlug}/business-stats`)
        .then(({ data }) => {
          this.globalTrustIndex = data.trustIndex
          this.globalIndustryRank = '#' + data.industryRank
          this.globalIndustryName = 'IN ' + data.industryCategory.toUpperCase()
          this.avgRatingStats = 'Average rating of ' + data.rating + ' from ' + data.reviews + ' reviews'
          this.avg_rating = this.generateDynamicRatings(data.rating)
          this.totalReviews = data.reviews
          this.npsTotal = data.nps_total
        })
        .catch(() => {
          flash(errMessage, 'is-danger')
          dataLayer.push({
            'userMessage': {
              'messageType': 'error',
              'messageName': 'Failed to get business stats on Business Profile view',
              'displayedMessage': errMessage
            }
          })
        })
    },
    /**
     * Get business billing details
     * @returns {void}
     */
    getBusinessBillingDetails () {
      const errMessage = 'We encountered a problem in an attempt to retrieve your business billing details, please try again later.'
      axios.get(`/${this.businessSlug}/billing-details`)
        .then(({ data }) => {
          this.currentActiveSubscription = data.subscription.name
        })
        .catch(() => {
          flash(errMessage, 'is-danger')
          dataLayer.push({
            'userMessage': {
              'messageType': 'error',
              'messageName': 'Failed to get business stats on Business Profile view',
              'displayedMessage': errMessage
            }
          })
        })
    },
    /**
     * Get all industries
     * @returns {void}
     */
    getIndustries () {
      new ApiResource({}).send(`/search/industries/`, {}, 'get')
        .then((industries) => {
          this.industries = industries
        })
        .catch(() => {
          flash('Error retrieving the list of industries! Contact the System\'s Administrator.', 'is-danger')
        })
    },
    /**
     * On click listener for when user selects an industry from drop down
     * @returns {null}
     * @param {industry} industry selected by user
     */
    selectIndustry (industry) {
      this.form.industry_id = industry.industry_id
      this.industry.name = industry.name
      this.industry.slug = industry.slug
      this.business_info.industry_id = industry.industry_id
      this.show_dropdown = false
      this.nothings_changed = false
      this.getSubIndustries(industry.slug)
    },
    /**
     * Hide the industries dropdown
     * @return {void}
     */
    hideDropdown () {
      this.show_dropdown = false
    },
    /**
     * Show the industries dropdown
     * @return {void}
     */
    showDropdown () {
      this.show_dropdown = !this.show_dropdown
    },
    /*
     * Get list of sub industries of a given industry
     * @param industrySlug
     * @returns {void}
     */
    getSubIndustries (industrySlug) {
      const errMessage = 'We encountered a problem in an attempt to retrieve your business industry sub categories, please try again later.'
      axios.get(`/industries/${industrySlug}/sub-ind`)
        .then(({ data }) => {
          this.subIndustries = data
        })
        .catch(() => {
          flash(errMessage, 'is-danger')
          dataLayer.push({
            'userMessage': {
              'messageType': 'error',
              'messageName': 'Failed to get sub industries on Business Profile view',
              'displayedMessage': errMessage
            }
          })
        })
    },
    /**
     * Stay on the page and close modal when clicked on stay on this page
     * @return {void}
     */
    stayOnPage () {
      this.showModal(false)
    },
    /**
     * Redirect to desired page when clicked on Leave this page
     * @return {void}
     */
    redirectRoute () {
      this.showModal(false)
      this.nothings_changed = true
      this.$router.push(this.routeToRedirect)
    },
    /**
     * Handle the dropdown event in mobile view
     * @param {object} option Option to open/close any section
     * @return {void}
     */
    handleDropbox (option) {
      this.dropbox[option] = !this.dropbox[option]
    }
  },
  /**
   * Event before navigating to desired route
   * @param {object} to Route where user want to navigate
   * @param {object} from Route where is coming from navigation
   * @param {function} next Navigating towards the intended route if true
   * @return {void}
   */
  beforeRouteLeave (to, from, next) {
    // Setting up object to get if page loaded first time
    if (from.name === this.$route.name) {
      let adminObj = {}
      Object.assign(adminObj, { ...JSON.parse(localStorage.getItem('admin')), isProfileLoadedFirstTime: false })
      localStorage.setItem('admin', JSON.stringify(adminObj))
    }
    if (!this.nothings_changed) {
      this.routeToRedirect = to.path
      this.showModal()
    } else {
      next()
    }
  }
}
</script>

<style lang="scss" scoped>
$color-white: #ffffff;
$light-text-color: #58595b;

.business-profile-section {
  color: $light-text-color;
  padding-bottom: 30px;
}

.page-info-section {
  position: relative;
  margin-bottom: 0.7rem;
}

.page-heading {
  color: $light-text-color;
  font-size: 12px;

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    padding: 25px;
  }
}

.horizontal-row {
  margin-top: 0;
  margin-bottom: 0rem;
  margin-left: -5%;
  margin-right: -5%;
}

.button {
  width: 100%;
}

.upload-avtar {
  font-size: 13px;
  font-weight: bold;
  color: $light-text-color;
  position: relative;
  line-height: 1.36;
  text-align: center;
  display: flex;
  justify-content: center;

  .tooltip-new {
    top: 15px;
    left: 105px;
    position: absolute;

    & > > > .tooltip__content-box {
      left: 49px;
      text-align: left;
    }

    /*Tablet view- (Ipad-Horizontal)*/
    @media screen and (min-width: 767px) and (max-width: 1000px) {
      left: 65px;
    }
    /*Tablet view- (Ipad-Vertical)*/
    @media screen and (min-width: 1024px) and (max-width: 1200px) {
      left: 88px;
    }
    /*Tablet view- (Ipad pro-Horizontal)*/
    @media screen and (min-width: 1366px) {
      left: 123px;
    }
    /*Large screens*/
    @media screen and (min-width: 1600px) {
      left: 130px;
    }
    /*Large screens*/
    @media screen and (min-width: 1900px) {
      left: 150px;
    }
    /*Large screens*/
    @media screen and (min-width: 2200px) {
      left: 190px;
    }
  }
}

.detail-section {
  padding: 0;

  .detail-section-row {
    align-items: center;
  }

  .company-name-mobile {
    display: flex;
    justify-content: center;

    .intro {
      p {
        /*Mobile view*/
        @media screen and (max-width: 767px) {
          font-size: 12px;
          .tooltip-new {
            top: 4px;
          }
        }
      }

      & > > > .tooltip-new {
        .tooltip__content-box {
          text-align: left;

          /*Mobile view*/
          @media screen and (max-width: 767px) {
            top: 40px;
            left: -120px;

            &::after {
              top: -20px;
              left: 137px;
              color: #f7f8f8;
              transform: rotate(361deg);
            }
          }
        }
      }

      /*Mobile view*/
      @media screen and (max-width: 767px) {
        padding: 25px;
      }
    }

    /*Mobile view*/
    @media screen and (max-width: 767px) {
      display: inline-flex;
      align-items: center;
    }
  }

  .logo-column {
    /*Mobile view*/
    @media screen and (max-width: 767px) {
      height: 160px;
      background-color: #f7f8f8;
    }
  }
}

.description {
  width: 100%;
  height: 100px;
  resize: none;
  color: $light-text-color;
  border: solid 1px rgba(88, 89, 91, 0.32);
  padding: 10px;

  &:focus {
    outline: none;
  }
}

.description-text {
  position: relative;
}

.description-first-text {
  color: $light-text-color;
  margin: 0 0 5px 0;
  font-size: 12px;
  line-height: 1.67;
}

.description-second-text {
  margin: 0 0 5px 0;
  font-size: 11px;
}

.words-left {
  top: 80px;
  right: 18px;
  bottom: 0;
  opacity: 0.6;
  position: absolute;
  font-size: 10px;
  line-height: 1.4;
}

.tooltip-new {
  top: 13px;
  left: 6px;
  width: 40px;
}

.cat-check {
  width: 17px;
  height: 17px;
  cursor: pointer;
  vertical-align: middle;
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
}

.description-section {
  /*Mobile view*/
  @media screen and (max-width: 767px) {
    margin: 0 17px 0 15px;
    padding-bottom: 0;
  }
}

.industries, .subcategories {
  .tooltip-new {
    top: 5px;
    left: 4px;

    & > > > .tooltip__content-box {
      left: 44px;
    }
  }

  .dropdown {
    width: 75%;
  }
}

.contact-section {
  .brief {
    font-weight: normal;
    font-size: 11px;
  }

  .input {
    height: 35px;
    font-size: 13px;
  }

  .address-info {
    .map-area {
      position: relative;

      .custom-marker-tooltip {
        display: none;
      }

      &:hover {
        .custom-marker-tooltip {
          top: 10%;
          left: 5%;
          color: $light-text-color;
          width: 40%;
          height: auto;
          min-height: 170px;
          display: block;
          z-index: 999;
          padding: 15px;
          font-size: 13px;
          position: absolute;
          word-break: break-all;
          background: $color-white;

          &:after {
            content: "";
            position: absolute;
            border-left: 12px solid transparent;
            border-right: 12px solid transparent;
            border-bottom: 12px solid #ffffff;
            transform: rotate(225deg);
            right: -15px;
            top: 40%;
          }

          /*Large screens*/
          @media screen and (min-width: 1600px) {
            top: 18%;
          }
          /*Large screens*/
          @media screen and (min-width: 1900px) {
            top: 23%;
          }
        }
      }
    }
  }

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    margin: 0 29px 0 28px;

    .address-info {
      .column {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
}

.online-profile-section {
  margin: 15px 0;

  .text-row {
    display: flex;
    align-items: baseline;
  }

  .label {
    color: $light-text-color;
    font-size: 12px;
    font-weight: bold;

    /*Mobile view*/
    @media screen and (max-width: 767px) {
      font-weight: 500;
    }
  }

  .related-icon {
    max-width: 10%;
    padding: 0;
    justify-content: end;

    /*Mobile view*/
    @media screen and (max-width: 767px) {
      max-width: 12%;
    }
  }

  .input {
    height: 35px;
    font-size: 13px;
    padding-left: 0.6em;

    &:focus {
      outline: none;
    }
  }

  .has-icons-left {
    display: flex;
  }

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    margin: 0 29px;

    .label {
      padding: 0 0 5px 0;
      margin: 0 0 5px 0;
    }
    .social-media-apps {
      .column {
        padding-right: 0;
      }
    }
    .social-info {
      display: none;
    }
    .show-social-info {
      display: block;
    }
  }
}

.disabled {
  opacity: 0.4;
}

.upgrade-section {
  font-size: 10px;
  font-weight: normal;

  a {
    color: #30aae1;
    text-decoration: underline;
  }

  .fa-lock {
    height: 15px;
    padding: 0 7px;
    vertical-align: sub;
  }
}

.industries-section {
  .is-flex-mobile {
    .tooltip-new {
      top: 4px;
    }
  }

  .button-dropdown {
    border: 1px solid rgba(88, 89, 91, 0.32);
    min-height: 35px !important;

    .is-size-6 {
      font-size: 12px !important;
    }

    &:focus {
      outline: none;
    }
  }

  & > > > .tooltip-new {
    .tooltip__content-box {
      left: 44px;
      z-index: 999 !important;

      /*Mobile view*/
      @media screen and (max-width: 767px) {
        max-width: 200px;
      }
    }
  }

  .subcategories {
    & > > > .tooltip-new {
      .tooltip__content-box {
        /*Mobile view*/
        @media screen and (max-width: 767px) {
          max-width: 185px;
        }
      }
    }
  }

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    margin: 0 29px 0 28px;

    .industries {
      .dropdown {
        width: 100%;
      }
    }
  }
}

.subcategories {
  .is-half {
    padding: 0 0 5px 0;
    font-size: 12px;
    position: relative;
  }

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    .upgrade-section {
      font-size: 9px;
    }
    .is-half {
      padding: 12px 12px 12px 0;
      display: inline-flex;
    }
  }
}

.action-section {
  /*Mobile view*/
  @media screen and (max-width: 767px) {
    margin: 60px 0 20px 0;
    .columns {
      display: flex;
      padding: 10px 23px 10px 0;
    }
  }
}

.is-half .fa-angle-right, .is-half .fa-angle-down {
  color: #8a8c91;
  font-size: 22px;
}

.button[disabled] {
  color: #fff !important;
  background-color: #CDCBCB !important;
}

.input[disabled] {
  color: $light-text-color;
  border: solid 1px rgba(88, 89, 91, 0.32);
  opacity: 0.4;
  background-color: #ffffff;

  &::placeholder {
    color: $light-text-color;
  }
}

/* Create a custom checkbox begins */
.checkmark {
  position: absolute;
  top: 2px;
  left: 0;
  height: 17px;
  width: 17px;
  z-index: -1;
  cursor: pointer;
  background-color: #fff;
  border: solid 1px #707070;

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    top: 12px;
  }
}

/* On mouse-over, add a grey background color */
.cat-list .is-narrow:hover input ~ .checkmark {
  background-color: #fff;
}

/* When the checkbox is checked, add a blue background */
.cat-list .is-narrow input:checked ~ .checkmark {
  border: none;
  background-color: #30aae1;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.cat-list .is-narrow input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.cat-list .is-narrow .checkmark:after {
  left: 7px;
  top: 2px;
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
}

@media screen and (max-width: 768px) {
  input[type=checkbox].cat-check:checked {
    background-color: transparent !important;
  }
}

/* Create a custom checkbox ends */

/*Helpers Begins*/
.active-dropbox {
  color: $light-text-color;
  opacity: 1;

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    font-weight: 700;
  }
}

.padding {
  &-0 {
    padding: 0;
  }

  &-top-0 {
    padding-top: 0;
  }

  &-right-0 {
    padding-right: 0;
  }

  &-left-0 {
    padding-left: 0;
  }
}

.margin {
  &-0 {
    margin: 0;
  }
}

.is-flex-align-center {
  display: flex;
  align-items: center;
}

/*Helpers Ends*/

/*General media queries*/
/*Mobile view*/
@media screen and (max-width: 767px) {
  .social-info {
    display: none;
  }
  .show-social-info {
    display: block;
  }
  .text-row {
    border-bottom: 1px solid rgba(88, 89, 91, 0.1);
  }
}

/*General media queries*/

/*Old CSS*/
.padding-left-75 {
  padding-left: 0.75em;
}

.margin-left-75 {
  margin-left: 0.75em;
}

p.line-height-1 {
  line-height: 1.2;
}

.is-white {
  background: white;
  padding: 20px;
}

.column.margin-20 {
  margin-top: 20px;
  margin-bottom: 20px;
}

.columns.box-shadow {
  box-shadow: 0px 0px 18px #dbdbdb;
}

.columns.padding-15-bottom-25 {
  padding-top: 15px;
  padding-bottom: 25px;
}

.columns:not(:last-child) {
  margin-bottom: 0;
}

div.desc {
  margin: 24px 0;
}

h6.title-business {
  margin-bottom: 20px;
}

.bg-color-f5f5f5 {
  background-color: #f5f5f5;
}

.padding-div {
  padding: 20px 20px 25px 0;
}

label {
  color: $light-text-color;
  font-size: 12px;
  font-weight: bold;

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    font-weight: 500;
  }
}

input {
  color: $light-text-color;
}

.icon.is-small {
  display: inline-flex !important;
}

.has-icons-left .is-left {
  background-color: #f5f5f5;
  margin: 2px;
}

.has-icons-left .icon {
  height: 2em;
}

.marging-top-8 {
  margin-top: 8px;
}

figure img.map {
  border: 1px solid #dbdbdb;
  background: #cbcbcc;
}

figure img {
  border-radius: 0;
}

.margin-bottom-6 {
  margin-bottom: 6px;
}

.preloader {
  padding-left: calc(0.625em - 1px);
  padding-right: calc(0.625em - 1px);
  padding-top: 0;
  padding-bottom: 0;
}

.circle-border, .circle-border:hover {
  background-color: #565e65;
}

.org_logo {
  border-radius: 50%;
}

.pick-logo-image {
  top: -83px;
  left: 28px;

  /*Mobile view*/
  @media screen and (max-width: 767px) {
    top: -50px;
    left: -94px;
  }
  /*Mobile view- (Iphones)*/
  @media screen and (min-width: 375px) {
    left: -95px;
  }
  /*Tablet view- (IPad-Vertical)*/
  @media screen and (min-width: 768px) {
    top: -120px;
    left: 20px;
  }
  /*Tablet view- (Horizontal)*/
  @media screen and (min-width: 1024px) {
    left: 35px;
    top: -85px;
  }
}

.disable-biz-name {
  pointer-events: none;
  opacity: 0.5;
}

.upgrade-text {
  color: #4a4a4a !important;
  margin-bottom: 1rem;
}

.upgrade-text > a, .upgrade-text > a:hover, .upgrade-text > a:visited {
  color: #4a4a4a !important;
  text-decoration: underline;
}

.button-dropdown {
  text-align: left;
}

.dropdown {
  display: block !important;
}

.dropdown-content {
  border-radius: 0;
  overflow-y: scroll;
  height: 200px;
}

.dropdown-menu {
  width: 100% !important;
}

.fa-angle-down {
  font-size: 1.4rem;
}

a.dropdown-item:hover {
  background-color: #30AAE1;
  color: #fff;
}

.industries {
  .fa-angle-down:before {
    border: 1px solid #95979b;
    border-right: 0;
    border-top: 0;
    content: " ";
    display: block;
    height: 0.5em;
    pointer-events: none;
    position: absolute;
    -webkit-transform: rotate(-45deg);
    transform: rotate(-45deg);
    -webkit-transform-origin: center;
    transform-origin: center;
    width: 0.5em;
    margin-top: -0.375em;
    right: 1.125em;
    top: 50%;
    z-index: 4;
  }
}

.cat-list {
  width: 100%;
  display: inline-flex;

  .columns {
    width: 100%;
    flex-wrap: wrap;
  }
}

.cat-item {
  margin-right: 20px;
}

.cat-name {
  margin-left: 5px;
  vertical-align: middle;
}
</style>

<!-- Global CSS Begins -->
<style lang="scss">
div#sideBar .is-padding-top-0 {
  /*Mobile view*/
  @media screen and (min-width: 800px) {
    padding-top: 0;
  }
}
</style>
<!-- Global CSS Ends -->



// WEBPACK FOOTER //
// src/pages/business/Profile.vue